import _ from 'lodash';
import { makeAutoObservable, observable } from 'mobx';
import moment from 'moment';
import GlobalNotification from '../../component/common/snackbar';
import { HeadCell } from '../../component/table/table.component';
import i18n from '../../i18n';
import { Condition } from '../../models/abstract/condition.entity';
import { OperationType, SensitiveRecord } from '../../models/sensitive-record.entity';
import { WarrantI18N } from '../../models/warrant-i18n.entity';
import { Warrant } from '../../models/warrant.entity';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import LSM from '../../modules/cache/tool-local-storage';
import apiManage from '../../request';
import { userData } from '../app.page';
import { ConditionType } from '../../component/form/search-key-input.component';
import { dateValidate, validateCurrentDate } from '../../constants/tool-order';
import { getAuthPointI18n } from '../../constants/tool-auth-point';

// 敏感操作记录数据管理
export class SensitiveRecordState implements SensitiveRecordData {

    constructor() {
        makeAutoObservable(this);
    }
    // *********************
    // Provider
    // *********************

    // 语言
    language: string = LSM.get(LOCAL_STORAGE_KEYS.LANGUAGE) || 'EN';
    // 下拉框初始默认值  条件搜索框
    selectValue: string = 'admin_nickname';
    // 下拉框初始值  授权操作类型下拉框
    opTypeSelectValue: string = OperationType.OP_ADD_WARRANT;

    // 敏感操作记录 列表数据
    sensitiveRecordList: SensitiveRecord[] = [];
    // 列表数据过滤额外传参
    extraParams: Condition = {};
    // 排序参数
    extraSortParams: {
        sort_key: string,
        sort_value: string,
        sort_control: number,
    } = { sort_key: '', sort_value: '', sort_control: 0 };
    // 搜索值
    searchValue: string = '';
    // 表格加载
    isTableLoading: boolean = false;
    // 表格数据数据总数
    dataTotal: number = 0;
    // 操作类型  静态文字数据
    sensitiveOpTypeData: Map<string, string> = new Map([
        [OperationType.OP_ADD_WARRANT, 'operation_record_addition_authorization'],
        [OperationType.OP_CANCEL_WARRANT, 'operation_record_delete_authorization'],
        [OperationType.OP_Add_GybDevice, 'operation_record_add_a_GYB_device'],
        [OperationType.OP_Delete_GybDevice, 'operation_record_delete_a_GYB_device'],
        [OperationType.OP_Default_GybDevice, 'operation_record_GYB_sets_default'],
        [OperationType.OP_Add_SecretKey, 'operation_record_add_function_key'],
        [OperationType.OP_Delete_SecretKey, 'operation_record_delete_function_key'],
        [OperationType.OP_Refresh_SecretKey, 'operation_record_refresh_function_key'],
        [OperationType.OP_Replace_Group, 'operation_record_replace_group'],
        [OperationType.OP_Delete_Staff, 'sensitive_operations_page_text_delete_staff'],
        [OperationType.OP_Add_Staff, 'ground_staff_component_text_new_create_employee'],
        [OperationType.OP_Update_Staff, 'ground_staff_component_text_edit_staff_account'],
        [OperationType.OP_Bind_Restaurant, 'ground_staff_component_text_associated_restaurant'],
        [OperationType.OP_Unbind_Restaurant, 'sensitive_operations_page_text_restaurant_unbind_staff'],
        [OperationType.OP_Update_Password, 'general_text_modify_password'],
        [OperationType.OP_Add_Reseller, 'general_text_add_reseller'],
        [OperationType.OP_Delete_Reseller, 'general_text_delete_reseller'],
        [OperationType.OP_Update_Reseller, 'general_text_update_reseller'],
        [OperationType.OP_Update_Cloud_Analysis_Origin, 'sensitive_operation_update_cloud_analysis_origin']
    ]);

    // 操作类型对象详情数据
    filterOpDataType: Record<OperationType, SensitiveOperateType> = {
        [OperationType.OP_ADD_WARRANT]: 'warrants',
        [OperationType.OP_CANCEL_WARRANT]: 'warrants',
        [OperationType.OP_Delete_GybDevice]: 'gyb_device',
        [OperationType.OP_Add_GybDevice]: 'gyb_device',
        [OperationType.OP_Default_GybDevice]: 'gyb_device',
        [OperationType.OP_Add_SecretKey]: 'secret_key',
        [OperationType.OP_Delete_SecretKey]: 'secret_key',
        [OperationType.OP_Refresh_SecretKey]: 'secret_key',
        [OperationType.OP_Replace_Group]: 'replace_group',
        [OperationType.OP_Delete_Staff]: 'delete_staff',
        [OperationType.OP_Add_Staff]: 'add_staff',
        [OperationType.OP_Update_Staff]: 'update_staff',
        [OperationType.OP_Bind_Restaurant]: 'bind_restaurant',
        [OperationType.OP_Unbind_Restaurant]: 'unbind_restaurant',
        [OperationType.OP_Update_Password]: 'update_password',
        [OperationType.OP_Add_Reseller]: 'add_reseller',
        [OperationType.OP_Delete_Reseller]: 'delete_reseller',
        [OperationType.OP_Update_Reseller]: 'update_reseller',
        [OperationType.OP_Update_Cloud_Analysis_Origin]: 'update_cloud_analysis_origin',
    };

    // 搜索条件
    conditions = observable<ConditionType>([]);
    // 每页数量
    perPage = 20;
    // 当前页码
    page = 0;
    // api传参开始时间
    startDate: string = '';
    // api传参结束时间
    endDate: string = '';
    // 时间组件显示操
    viewDateTimeData: {
        // 开始时间
        startDate: Date | null,
        // 结束时间
        endDate: Date | null,
    } = {
            startDate: null,
            endDate: null
        }

    // 设置搜索条件
    setConditions(val: ConditionType[]) {
        this.conditions.replace(val);
        this.refreshData();
    }

    // 设置语言
    setLanguage(value: string) {
        this.language = value;
    }

    // 获取表头排序状态值
    getSortStatus(key: string) {
        const listSort = this.extraSortParams;
        if (key === listSort.sort_key) {
            return listSort.sort_control
        }
        return 1;
    }

    // 初始化
    async init() {
        // 初始化默认为倒序
        const params: any = { sort_value: '-1' };
        this.setSortParams(params);
        // 时间默认今天
        const { startTime, endTime } = validateCurrentDate();
        this.viewDateTimeData.startDate = moment(startTime, 'YYYY-MM-DD HH:mm').toDate();
        this.viewDateTimeData.endDate = moment(endTime, 'YYYY-MM-DD HH:mm').toDate();
        this.startDate = moment(startTime, 'YYYY-MM-DD HH:mm').toISOString()
        this.endDate = moment(endTime, 'YYYY-MM-DD HH:mm').toISOString()
        await this.getRecordList();
    }

    // 更新表格数据
    updateData(data: SensitiveRecord[], totalCount: number) {
        this.sensitiveRecordList = data || [];
        this.dataTotal = totalCount;
    }

    // 获取敏感操作记录数据
    async getRecordList(extra?: SensitiveRecord & Condition) {
        // 关闭加载
        this.loadTable();
        try {
            const searchObj = {};
            this.conditions.forEach((item) => {
                searchObj[item.key] = searchObj[item.key] ? _.concat(searchObj[item.key], item.value) : [item.value];
            })
            // 发送的参数
            const params = {
                pagination_per_page: this.perPage,
                pagination_page: this.page + 1,
                key: LSM.get(LOCAL_STORAGE_KEYS.LANGUAGE) === 'CN' ? 'CN' : 'EN',
                ...this.extraParams,
                ...searchObj,
                ...extra,
                sort_value: +this.extraSortParams.sort_value,
                start_time: this.startDate,
                end_time: this.endDate,
            }

            const result = await apiManage.get_sensitive_operation_record(params);
            if (!result.error) {
                // 解构数据
                const { dbResults, totalCount } = result[0];
                this.updateData(dbResults || [], _.get(totalCount, '0.count', 0))
            } else {
                GlobalNotification.error(`${i18n.t('operation_record_Failed_to_obtain_the_sensitive_operation_record')}`);
            }

        } catch (error) {
            GlobalNotification.error(`${i18n.t('operation_record_Failed_to_obtain_the_sensitive_operation_record')}`);
        }
        // 开启加载
        this.loadedTable();
    }

    // 设置获取数据额外传参
    setExtraParams(extraParams: typeof this.extraParams) {
        this.extraParams = extraParams;
    }

    /**
     * 设置排序参数
     * @param val 排序参数
     */
    setSortParams(val: typeof this.extraSortParams) {
        this.extraSortParams = val;
    }

    /**
     * 表格排序  优化重写
     * @param headCell 排序表头字段
     * @var headCell.sortStatus 2表示正序 1表示倒序 0默认倒序
     * 1.排序
     * 2.保存参数
     * 3.改变头部状态
     * 排序状态：2表示正序 1表示倒序 0默认倒序
     */
    async _handleSort(headCell: HeadCell) {
        // 排序值 value 1 正序  -1 倒序
        let sortValue = '';
        let sortControl = 0;
        sortValue = headCell.sortStatus === 0 ? '1' : headCell.sortStatus === 1 ? '1' : '-1';
        sortControl = headCell.sortStatus === 0 ? 1 : headCell.sortStatus === 1 ? 2 : 1;
        // 设置排序表头状态
        this.setSortParams({
            sort_key: sortControl === 0 ? '' : headCell.id,
            sort_value: sortValue,
            sort_control: sortControl
        });
        // 请求参数
        const params = sortControl !== 0 ? {
            sort_key: 'operation_time',
            sort_value: this.extraSortParams.sort_value,
        } : {};
        // 请求数据
        await this.refreshData(params);
    }

    // 刷新数据用
    async refreshData(extraParams?: any) {
        this.page = 0;
        await this.getRecordList(extraParams);
    }


    // 设置下拉框的值  搜索条件下拉框
    async setSelectVal(value: string) {
        this.selectValue = value;
        let extraParams: any;
        // let key = value;
        switch (value) {
            case 'operation_type':
                extraParams = {
                    [value]: this.opTypeSelectValue,
                };
                await this.refreshData(extraParams);
                break;
        }
        // 清空搜索值
        this.updateSearchValue('');
        this.setExtraParams({
            ...extraParams,
            sort_value: +this.extraSortParams.sort_value,
        });
    }

    // 设置下拉框的值  授权操作类型下拉框
    async setOpTypeSelectVal(value: typeof this.opTypeSelectValue) {
        this.opTypeSelectValue = value;
        const key = this.selectValue;
        const extraParams = {
            [key]: value,
            sort_value: +this.extraSortParams.sort_value,
        };
        this.setExtraParams(extraParams);
        await this.refreshData(extraParams);
    }

    // 更新搜索值
    updateSearchValue(value: string) {
        this.searchValue = value;
    }


    /**
     * 显示的操作类型
     * @param opType
     * @returns
     */
    showOpType(opType: string) {
        return this.sensitiveOpTypeData.get(opType);
    }


    // 获取授权信息需要展示的数据
    getWarrantInfo(warrants: Warrant[] = []): string {
        // 是否为授权集合
        const isCollection = warrants?.[0]?.warrants ? true : false;
        // 授权点要展示的数据
        const singleWarrant: string[] = [];
        // 授权包要展示的数据
        const warrantCollections: string[] = [];
        // 国际化语言
        const lang = userData.language;
        // 存在授权集合
        if (isCollection) {
            const warrantsI18N: WarrantI18N[] = warrants.map((warrant) => (Object.assign(warrant?.warranti18N || {}, { warrants: warrant.warrants }) || {}))
            warrantsI18N.forEach(i18nItem => {
                i18nItem.name = i18nItem?.name?.filter(i => {
                    return i.key === lang;
                });
                // 授权集合数据筛选
                i18nItem.warrants?.forEach((item: Warrant) => {
                    // 找出授权集合国际化
                    const auName = getAuthPointI18n(item.warranti18N?.name || [])
                    // 授权集合名称
                    const showStr = `${auName}(${moment(item?.limit_point?.start_date).format('YYYY-MM-DD')} ~ ${moment(item?.limit_point?.end_date).format('YYYY-MM-DD')})`;
                    warrantCollections.push(showStr);
                });
                // 在数据开头前面加入授权包的名字，用以区别
                const name = `${warrantsI18N?.[0]?.name?.[0]?.value || ''}`;
                if (name) {
                    warrantCollections.unshift()
                }

            })
        } else {
            // 存在授权点
            const warrantsI18N: WarrantI18N[] = warrants.map((warrant) => (Object.assign(warrant?.warranti18N || {}, { limit_point: warrant?.limit_point }) || {}));
            // 循环过滤出符合当前选择的语言的授权数据
            warrantsI18N?.forEach(item => {
                item.name = item?.name?.filter(i => {
                    return i.key === lang;
                });
                // 授权信息三个点的展示数据  显示是否过期
                const showStr = `${item?.name?.[0]?.value || ''}(${moment(item?.limit_point?.start_date).format('YYYY-MM-DD')} ~ ${moment(item?.limit_point?.end_date).format('YYYY-MM-DD')})`;
                singleWarrant.push(showStr);
            });
        }
        // 如果为授权集合 则显示授权集合的数据

        const showData = isCollection ? warrantCollections : singleWarrant;
        return showData.join(',');
    }

    // 开启表格加载
    loadTable() {
        this.isTableLoading = true;
    }

    // 关闭表格加载
    loadedTable() {
        this.isTableLoading = false;
    }

    /**
     * 分页
     * @param params
     */
    changePageCallback(params: { page: number; curRowsPerPage: number }) {
        this.page = params.page
        this.perPage = params.curRowsPerPage;
        this.getRecordList()
    }

    /**
     * 时间选择
     * @param date
     */
    handleChangePicker(dates: [Date | null, Date | null] | undefined) {
        // 开始时间
        this.viewDateTimeData.startDate = dates?.[0] ?? null;
        // 结束时间
        this.viewDateTimeData.endDate = dates?.[1] ?? null;
        if (dates) {
            if (dates[0] && dates[1] && dateValidate(dates[0], dates[1])) {
                this.startDate = moment(dates[0]).toISOString();
                this.endDate = moment(dates[1]).toISOString();
                this.page = 0;
                this.getRecordList()
            }
        }
    }



}

// 数据格式
type SensitiveRecordData = {
    // 下拉框初始默认值
    selectValue: string;
}

/**
 * 敏感操作授权类型
 * @typedef warrant 授权操作相关
 * @typedef gybDevice gyb设备操作相关
 * @typedef secret_key 密钥操作相关
 * @typedef replace_group 更换集团操作相关
 * @typedef delete_staff  删除员工操作相关
 * @typedef add_staff  新增员工操作相关
 * @typedef update_staff  编辑员工操作相关
 * @typedef update_password  更换密码操作相关
 * @typedef bind_restaurant  绑定餐馆操作相关
 * @typedef unbind_restaurant  解绑餐馆操作相关
 */
type SensitiveOperateType = 'warrants' | 'gyb_device' | 'secret_key'
    | 'replace_group' | 'delete_staff' | 'add_staff' | 'update_staff' | 'bind_restaurant' | 'unbind_restaurant' | 'update_password' | 'add_reseller' | 'delete_reseller' | 'update_reseller' | 'update_cloud_analysis_origin';


